// Web Audio engine: metronome, ambient pads, step sequencer (drums/shaker). Real synthesis.

let _ctx = null;
let _noise = null;
let _unlocked = false;

export function ac() {
  if (!_ctx) {
    _ctx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
}

export function audioState() {
  return _ctx ? _ctx.state : "none";
}

export function audioBlocked() {
  return !!_ctx && _ctx.state !== "running";
}

// Resume + prime the context synchronously inside a user gesture, and await it.
export async function unlockAudio() {
  const ctx = ac();
  try {
    if (ctx.state === "suspended") await ctx.resume();
    const b = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = b;
    src.connect(ctx.destination);
    src.start(0);
    _unlocked = true;
  } catch (e) { /* noop */ }
  return ctx.state;
}

// Unlock audio on the first user gesture (browsers block autoplay until then).
function _unlock() {
  if (_unlocked && _ctx && _ctx.state === "running") return;
  unlockAudio();
}
if (typeof window !== "undefined") {
  ["pointerdown", "touchend", "mousedown", "keydown", "click"].forEach((ev) =>
    window.addEventListener(ev, _unlock, { passive: true })
  );
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && _ctx && _ctx.state === "suspended") _ctx.resume();
  });
}

function noiseBuffer(ctx) {
  if (_noise) return _noise;
  const len = ctx.sampleRate * 1.0;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  _noise = buf;
  return buf;
}

function env(ctx, dest, t, peak, attack, decay) {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.connect(dest);
  return g;
}

function tone(ctx, dest, t, freq, type, peak, dur) {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  const g = env(ctx, dest, t, peak, 0.002, dur);
  o.connect(g);
  o.start(t);
  o.stop(t + dur + 0.05);
}

function noiseHit(ctx, dest, t, dur, filterType, freq, peak) {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  const f = ctx.createBiquadFilter();
  f.type = filterType;
  f.frequency.value = freq;
  const g = env(ctx, dest, t, peak, 0.001, dur);
  src.connect(f);
  f.connect(g);
  src.start(t);
  src.stop(t + dur + 0.05);
}

// ---- Voices ----
export const VOICES = {
  click: (ctx, d, t, accent) => tone(ctx, d, t, accent ? 1600 : 1000, "sine", accent ? 0.6 : 0.4, 0.05),
  wood: (ctx, d, t, accent) => tone(ctx, d, t, accent ? 1200 : 900, "triangle", accent ? 0.6 : 0.4, 0.06),
  electronic: (ctx, d, t, accent) => tone(ctx, d, t, accent ? 2000 : 1400, "square", accent ? 0.35 : 0.22, 0.04),
  shimmer: (ctx, d, t, accent) => { tone(ctx, d, t, accent ? 2400 : 1800, "sine", 0.25, 0.25); noiseHit(ctx, d, t, 0.25, "highpass", 6000, 0.12); },
  clap: (ctx, d, t) => { for (let i = 0; i < 3; i++) noiseHit(ctx, d, t + i * 0.012, 0.09, "bandpass", 1500, 0.4); },
  kick: (ctx, d, t) => {
    const o = ctx.createOscillator(); o.type = "sine";
    o.frequency.setValueAtTime(150, t); o.frequency.exponentialRampToValueAtTime(48, t + 0.14);
    const g = env(ctx, d, t, 0.9, 0.002, 0.22); o.connect(g); o.start(t); o.stop(t + 0.3);
  },
  snare: (ctx, d, t) => { noiseHit(ctx, d, t, 0.16, "highpass", 1800, 0.5); tone(ctx, d, t, 180, "triangle", 0.25, 0.1); },
  hat: (ctx, d, t) => noiseHit(ctx, d, t, 0.045, "highpass", 8000, 0.28),
  openhat: (ctx, d, t) => noiseHit(ctx, d, t, 0.22, "highpass", 8000, 0.24),
  tom: (ctx, d, t) => { const o = ctx.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(220, t); o.frequency.exponentialRampToValueAtTime(110, t + 0.18); const g = env(ctx, d, t, 0.6, 0.002, 0.24); o.connect(g); o.start(t); o.stop(t + 0.3); },
  crash: (ctx, d, t) => noiseHit(ctx, d, t, 0.7, "highpass", 5000, 0.3),
  perc: (ctx, d, t) => tone(ctx, d, t, 520, "triangle", 0.4, 0.12),
  shaker: (ctx, d, t, accent) => noiseHit(ctx, d, t, accent ? 0.09 : 0.06, "highpass", 7000, accent ? 0.35 : 0.2),
  tambourine: (ctx, d, t, accent) => { for (let i = 0; i < 4; i++) noiseHit(ctx, d, t + i * 0.006, 0.08, "highpass", 9000, accent ? 0.25 : 0.15); },
  stick: (ctx, d, t) => tone(ctx, d, t, 1000, "square", 0.3, 0.03),
  conga: (ctx, d, t) => { const o = ctx.createOscillator(); o.type = "sine"; o.frequency.setValueAtTime(320, t); o.frequency.exponentialRampToValueAtTime(200, t + 0.1); const g = env(ctx, d, t, 0.5, 0.002, 0.16); o.connect(g); o.start(t); o.stop(t + 0.2); },
  clave: (ctx, d, t) => tone(ctx, d, t, 2500, "sine", 0.4, 0.04),
};

// ---- Metronome ----
export class Metronome {
  constructor() {
    this.bpm = 72; this.beats = 4; this.sub = 1; this.sound = "click"; this.accent = true;
    this.vol = 0.9; this.running = false; this.onBeat = null;
    this._next = 0; this._beat = 0; this._timer = null; this._gain = null;
  }
  start() {
    if (this.running) return;
    const ctx = ac();
    this._gain = ctx.createGain(); this._gain.gain.value = this.vol; this._gain.connect(ctx.destination);
    this.running = true; this._beat = 0; this._next = ctx.currentTime + 0.06;
    this._loop();
  }
  _loop() {
    if (!this.running) return;
    const ctx = ac();
    const spb = 60.0 / this.bpm / this.sub;
    while (this._next < ctx.currentTime + 0.12) {
      const isDown = this._beat % (this.beats * this.sub) === 0;
      const accent = this.accent && isDown;
      const isMain = this._beat % this.sub === 0;
      const voice = VOICES[this.sound] || VOICES.click;
      if (isMain || this.sub === 1) voice(ctx, this._gain, this._next, accent);
      else VOICES.hat(ctx, this._gain, this._next, false);
      if (this.onBeat) {
        const b = Math.floor(this._beat / this.sub) % this.beats;
        const t = this._next; const cur = ctx.currentTime;
        setTimeout(() => this.onBeat && this.onBeat(b, isDown), Math.max(0, (t - cur) * 1000));
      }
      this._beat++; this._next += spb;
    }
    this._timer = setTimeout(() => this._loop(), 25);
  }
  stop() { this.running = false; clearTimeout(this._timer); if (this._gain) try { this._gain.disconnect(); } catch (e) {} }
  setBpm(b) { this.bpm = b; }
  setVol(v) { this.vol = v; if (this._gain) this._gain.gain.value = v; }
}

// ---- Ambient Pad ----
const BASEN = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
function keyMidi(root) {
  const letter = root[0].toUpperCase();
  let idx = BASEN[letter] ?? 0;
  for (const c of root.slice(1)) { if (c === "#") idx++; else if (c === "b") idx--; }
  return 48 + idx; // octave 3
}
function midiFreq(m) { return 440 * Math.pow(2, (m - 69) / 12); }

const PAD_TONE = { Warm: "triangle", Atmospheric: "sine", Cinematic: "sawtooth", Prayer: "sine", Ambient: "sine", Dark: "triangle", Bright: "sawtooth" };
const PAD_CUTOFF = { Warm: 1200, Atmospheric: 900, Cinematic: 1800, Prayer: 700, Ambient: 1000, Dark: 500, Bright: 3000 };

export class Pad {
  constructor() { this.vol = 0.5; this.current = null; }
  _build(key, category) {
    const ctx = ac();
    const type = PAD_TONE[category] || "sine";
    const master = ctx.createGain(); master.gain.value = 0.0001;
    const filter = ctx.createBiquadFilter(); filter.type = "lowpass";
    filter.frequency.value = PAD_CUTOFF[category] || 1000;
    filter.connect(master); master.connect(ctx.destination);
    const root = keyMidi(key);
    const intervals = [0, 7, 12, 16, 19];
    const oscs = [];
    intervals.forEach((iv, i) => {
      const f = midiFreq(root + iv);
      [f, f * 1.004].forEach((ff) => {
        const o = ctx.createOscillator(); o.type = type; o.frequency.value = ff;
        const g = ctx.createGain(); g.gain.value = (i === 0 ? 0.3 : 0.16) / (1 + i * 0.3);
        o.connect(g); g.connect(filter); o.start(); oscs.push(o);
      });
    });
    // slow LFO on filter for movement
    const lfo = ctx.createOscillator(); lfo.frequency.value = 0.08;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 200;
    lfo.connect(lfoGain); lfoGain.connect(filter.frequency); lfo.start();
    return { master, oscs, lfo, key, category };
  }
  play(key, category = "Warm", fade = 2.5) {
    const ctx = ac();
    this.stop(fade);
    const node = this._build(key, category);
    node.master.gain.linearRampToValueAtTime(this.vol, ctx.currentTime + fade);
    this.current = node;
  }
  crossfade(key, category, fade = 3) { this.play(key, category, fade); }
  setVolume(v) { this.vol = v; if (this.current) this.current.master.gain.linearRampToValueAtTime(v, ac().currentTime + 0.2); }
  stop(fade = 2) {
    const node = this.current; this.current = null;
    if (!node) return;
    const ctx = ac();
    node.master.gain.cancelScheduledValues(ctx.currentTime);
    node.master.gain.setValueAtTime(Math.max(node.master.gain.value, 0.0001), ctx.currentTime);
    node.master.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + fade);
    setTimeout(() => { node.oscs.forEach((o) => { try { o.stop(); } catch (e) {} }); try { node.lfo.stop(); } catch (e) {} }, fade * 1000 + 100);
  }
}

// ---- Step Sequencer (drums + shaker) ----
export class Sequencer {
  constructor(steps = 16) {
    this.bpm = 90; this.steps = steps; this.swing = 0; this.vol = 0.9;
    this.pattern = {}; this.running = false; this.onStep = null;
    this._next = 0; this._step = 0; this._timer = null; this._gain = null;
  }
  start() {
    if (this.running) return;
    const ctx = ac();
    this._gain = ctx.createGain(); this._gain.gain.value = this.vol; this._gain.connect(ctx.destination);
    this.running = true; this._step = 0; this._next = ctx.currentTime + 0.06; this._loop();
  }
  _loop() {
    if (!this.running) return;
    const ctx = ac();
    const sixteenth = 60.0 / this.bpm / 4;
    while (this._next < ctx.currentTime + 0.12) {
      const step = this._step % this.steps;
      const swingOffset = (step % 2 === 1) ? this.swing * sixteenth * 0.5 : 0;
      const at = this._next + swingOffset;
      Object.keys(this.pattern).forEach((ch) => {
        if (this.pattern[ch] && this.pattern[ch][step]) {
          const v = VOICES[ch] || VOICES.perc;
          v(ctx, this._gain, at, step % 4 === 0);
        }
      });
      if (this.onStep) {
        const t = at, cur = ctx.currentTime;
        setTimeout(() => this.onStep && this.onStep(step), Math.max(0, (t - cur) * 1000));
      }
      this._step++; this._next += sixteenth;
    }
    this._timer = setTimeout(() => this._loop(), 25);
  }
  stop() { this.running = false; clearTimeout(this._timer); if (this._gain) try { this._gain.disconnect(); } catch (e) {} }
  setVol(v) { this.vol = v; if (this._gain) this._gain.gain.value = v; }
}

export function playVoice(name) {
  const ctx = ac();
  const g = ctx.createGain(); g.gain.value = 0.9; g.connect(ctx.destination);
  (VOICES[name] || VOICES.perc)(ctx, g, ctx.currentTime + 0.02, true);
}
